import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/components/ui/use-toast';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from '@/api/business';
import { updateMarketResearch } from '@/api/business/marketResearch';
import { Checkbox } from '@/components/ui/checkbox';
import Button from '@/components/Button';
import { Input } from '@/components/ui/input';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { CalendarIcon, ChevronsUpDown } from 'lucide-react';
import { Calendar } from '@/components/ui/calendar';
import { cn } from '@/lib/utils';
import { format, parse } from 'date-fns';
import { useResaleQuery } from '@/queries/useResaleQuery';
import { MarketResearchKeys } from '@/queries/useMarketResearchQuery';
import { MarketResearchByIdKeys } from '@/queries/useMarketResearchByIdQuery';
import { MarketResearch } from '@/types/MarketResearch';
import { useEffect } from 'react';

interface EditDialogProps {
  isOpen: boolean;
  onClose: () => void;
  data: MarketResearch;
}

const parseSkTempoToDate = (skTempo: number): Date => {
  const str = skTempo.toString();
  return parse(str, 'yyyyMMdd', new Date());
};

const EditMarketResearchDialog = ({
  isOpen,
  onClose,
  data,
}: EditDialogProps) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { data: DataResale } = useResaleQuery();
  const isDraft = data.DD_STATUS === 'Rascunho';

  const schema = z.object({
    NM_PESQUISA: z
      .string()
      .min(1, {
        message: 'O campo nome da pesquisa é obrigatório',
      })
      .max(150, 'Quantidade máxima de 150 caracteres'),
    SK_REVENDAS: z.array(z.number()).min(1, {
      message: 'Ao menos uma revenda é obrigatória',
    }),
    SK_TEMPO_INICIO: z.date({
      required_error: 'Data de inicio é obrigatório',
    }),
    SK_TEMPO_FIM: z.date({
      required_error: 'Data final é obrigatório',
    }),
    DD_TIPO_CICLO: z.string().min(1, {
      message: 'O campo tipo ciclo é obrigatório',
    }),
    DD_TIPO_ENCERRAMENTO: z.string().min(1, {
      message: 'O campo tipo encerramento é obrigatório',
    }),
    QTD_DIAS_CARENCIA: z.string(),
    DD_COLETA_IMAGEM: z.number(),
  });

  const getInitialRevendas = (d: MarketResearch): number[] => {
    if (d.SK_REVENDAS && d.SK_REVENDAS.length > 0) return d.SK_REVENDAS;
    if (d.REVENDAS && d.REVENDAS.length > 0)
      return d.REVENDAS.map((r) => r.SK_REVENDA);
    if (d.SK_REVENDA) return [d.SK_REVENDA];
    return [];
  };

  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: {
      NM_PESQUISA: data.NM_PESQUISA || '',
      SK_REVENDAS: getInitialRevendas(data),
      SK_TEMPO_INICIO: data.SK_TEMPO_INICIO
        ? parseSkTempoToDate(data.SK_TEMPO_INICIO)
        : new Date(),
      SK_TEMPO_FIM: data.SK_TEMPO_FIM
        ? parseSkTempoToDate(data.SK_TEMPO_FIM)
        : new Date(),
      DD_TIPO_CICLO: data.DD_TIPO_CICLO || '',
      DD_TIPO_ENCERRAMENTO: data.DD_TIPO_ENCERRAMENTO || '',
      QTD_DIAS_CARENCIA: data.QTD_DIAS_CARENCIA?.toString() || '0',
      DD_COLETA_IMAGEM: data.DD_COLETA_IMAGEM || 0,
    },
  });

  useEffect(() => {
    if (data && isOpen) {
      form.reset({
        NM_PESQUISA: data.NM_PESQUISA || '',
        SK_REVENDAS: getInitialRevendas(data),
        SK_TEMPO_INICIO: data.SK_TEMPO_INICIO
          ? parseSkTempoToDate(data.SK_TEMPO_INICIO)
          : new Date(),
        SK_TEMPO_FIM: data.SK_TEMPO_FIM
          ? parseSkTempoToDate(data.SK_TEMPO_FIM)
          : new Date(),
        DD_TIPO_CICLO: data.DD_TIPO_CICLO || '',
        DD_TIPO_ENCERRAMENTO: data.DD_TIPO_ENCERRAMENTO || '',
        QTD_DIAS_CARENCIA: data.QTD_DIAS_CARENCIA?.toString() || '0',
        DD_COLETA_IMAGEM: data.DD_COLETA_IMAGEM || 0,
      });
    }
  }, [data, isOpen, form]);

  const { mutate, isPending: mutateLoading } = useMutation({
    mutationFn: updateMarketResearch,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: MarketResearchKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: MarketResearchByIdKeys.details(),
      });
      onClose();
      toast({
        title: 'Pesquisa atualizada com sucesso',
      });
    },
    onError: (err) => {
      let errorMessage =
        'Não foi possível atualizar a pesquisa. Tente novamente mais tarde.';

      if (isAxiosError(err)) {
        errorMessage = err.response?.data.message || errorMessage;
      }

      toast({
        title: 'Erro ao atualizar a pesquisa',
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });

  const onSubmit = (formData: z.infer<typeof schema>) => {
    mutate({
      SK_PESQUISA: data.SK_PESQUISA,
      NM_PESQUISA: formData.NM_PESQUISA,
      SK_REVENDAS: formData.SK_REVENDAS,
      SK_REVENDA: formData.SK_REVENDAS[0],
      SK_TEMPO_INICIO: Number(
        formData.SK_TEMPO_INICIO.toISOString().slice(0, 10).replace(/-/g, ''),
      ),
      SK_TEMPO_FIM: Number(
        formData.SK_TEMPO_FIM.toISOString().slice(0, 10).replace(/-/g, ''),
      ),
      DD_COLETA_IMAGEM: formData.DD_COLETA_IMAGEM,
      DD_TIPO_ENCERRAMENTO: formData.DD_TIPO_ENCERRAMENTO,
      QTD_DIAS_CARENCIA: Number(formData.QTD_DIAS_CARENCIA),
      DD_TIPO_CICLO: formData.DD_TIPO_CICLO,
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[600px] overflow-auto">
        <DialogHeader className="px-2">
          <DialogTitle>Editar pesquisa</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-2 px-2 overflow-hidden overflow-y-auto"
          >
            <div className="grid grid-cols-[1fr] gap-4">
              <FormField
                name="NM_PESQUISA"
                control={form.control}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nome da pesquisa</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="grid grid-cols-[1fr,1fr] gap-4">
              <FormField
                name="SK_REVENDAS"
                control={form.control}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Revendas participantes{' '}
                      {!isDraft && '(Bloqueado após publicação)'}
                    </FormLabel>
                    <Popover>
                      <PopoverTrigger asChild disabled={!isDraft}>
                        <FormControl>
                          <Button
                            variant="outline"
                            disabled={!isDraft}
                            className="w-full justify-between font-normal text-left h-auto min-h-[40px] px-3 py-2"
                          >
                            <span className="truncate">
                              {field.value && field.value.length > 0
                                ? DataResale?.filter((r) =>
                                    field.value.includes(r.SK_REVENDA),
                                  )
                                    .map((r) => r.DS_REVENDA)
                                    .join(', ') || data.DS_REVENDA
                                : data.DS_REVENDA || 'Selecionar Revendas'}
                            </span>
                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-[300px] p-2" align="start">
                        <div className="flex items-center space-x-2 pb-2 mb-2 border-b">
                          <Checkbox
                            id="select-all-resales-edit"
                            checked={Boolean(
                              DataResale &&
                              DataResale.length > 0 &&
                              field.value?.length === DataResale.length,
                            )}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                field.onChange(
                                  DataResale?.map((r) => r.SK_REVENDA) || [],
                                );
                              } else {
                                field.onChange([]);
                              }
                            }}
                          />
                          <label
                            htmlFor="select-all-resales-edit"
                            className="text-xs font-medium cursor-pointer"
                          >
                            Selecionar todas ({DataResale?.length || 0})
                          </label>
                        </div>
                        <div className="max-h-[200px] overflow-y-auto space-y-1">
                          {DataResale?.map((option) => {
                            const isSelected = field.value?.includes(
                              option.SK_REVENDA,
                            );
                            return (
                              <div
                                key={option.SK_REVENDA}
                                className="flex items-center space-x-2 p-1.5 rounded hover:bg-gray-100 cursor-pointer"
                                onClick={() => {
                                  if (isSelected) {
                                    field.onChange(
                                      field.value.filter(
                                        (id) => id !== option.SK_REVENDA,
                                      ),
                                    );
                                  } else {
                                    field.onChange([
                                      ...(field.value || []),
                                      option.SK_REVENDA,
                                    ]);
                                  }
                                }}
                              >
                                <Checkbox
                                  checked={isSelected}
                                  onCheckedChange={() => {}}
                                />
                                <span className="text-xs text-gray-800 truncate">
                                  {option.DS_REVENDA}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                name="DD_TIPO_CICLO"
                control={form.control}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tipo ciclo</FormLabel>
                    <Select
                      onValueChange={(value) => field.onChange(value)}
                      value={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="Diário">Diário</SelectItem>
                        <SelectItem value="Semanal">Semanal</SelectItem>
                        <SelectItem value="Quinzenal">Quinzenal</SelectItem>
                        <SelectItem value="Mensal">Mensal</SelectItem>
                        <SelectItem value="Período">Período</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="grid md:grid-cols-2 grid-cols-1 gap-4">
              <FormField
                name="SK_TEMPO_INICIO"
                control={form.control}
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>Data de inicio</FormLabel>
                    <Popover>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant={'outline'}
                            className={cn(
                              'w-full pl-3 text-left font-normal',
                              !field.value && 'text-muted-foreground',
                            )}
                          >
                            {field.value ? (
                              format(field.value, 'PPP')
                            ) : (
                              <span>Selecionar Data</span>
                            )}
                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={field.value}
                          onSelect={field.onChange}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                name="SK_TEMPO_FIM"
                control={form.control}
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>Data final</FormLabel>
                    <Popover>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant={'outline'}
                            className={cn(
                              'w-full pl-3 text-left font-normal',
                              !field.value && 'text-muted-foreground',
                            )}
                          >
                            {field.value ? (
                              format(field.value, 'PPP')
                            ) : (
                              <span>Selecionar Data</span>
                            )}
                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={field.value}
                          onSelect={field.onChange}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="grid grid-cols-[1fr,1fr] gap-4">
              <FormField
                name="DD_TIPO_ENCERRAMENTO"
                control={form.control}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tipo encerramento</FormLabel>
                    <Select
                      onValueChange={(value) => field.onChange(value)}
                      value={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="Manual">Manual</SelectItem>
                        <SelectItem value="Automático">Automático</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {form.watch('DD_TIPO_ENCERRAMENTO') == 'Automático' && (
                <FormField
                  name="QTD_DIAS_CARENCIA"
                  control={form.control}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Dias de carência</FormLabel>
                      <FormControl>
                        <Input {...field} type="number" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="DD_COLETA_IMAGEM"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                    <FormControl>
                      <Checkbox
                        checked={Boolean(field.value)}
                        onCheckedChange={(checked) =>
                          field.onChange(checked ? 1 : 0)
                        }
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel>Inclusão de foto obrigatório?</FormLabel>
                      <FormMessage />
                    </div>
                  </FormItem>
                )}
              />
            </div>
            <DialogFooter>
              <Button type="button" onClick={onClose} variant="ghost">
                Cancelar
              </Button>
              <Button type="submit" isLoading={mutateLoading}>
                Salvar
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default EditMarketResearchDialog;
